import React, { useEffect, useState } from 'react';
import { FileText, Download, Loader2, PenLine, Calendar, Home, FileSignature, FileCheck2, ExternalLink, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useTenant } from './TenantContext';
import { tenantAPI } from '../../lib/api';
import { toast } from 'sonner';

const TenantContract = () => {
  const { profile, downloading, handleDownloadLeaseContract, handleDownloadResidenceCertificate, openSignModal } = useTenant();
  const [signedInfo, setSignedInfo] = useState(null);
  const [avenants, setAvenants] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(true);
  const [busyAvenant, setBusyAvenant] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [sigRes, avRes] = await Promise.all([
          tenantAPI.getSignedContractInfo().catch(() => ({ data: { available: false } })),
          tenantAPI.getRentIncreases().catch(() => ({ data: [] })),
        ]);
        setSignedInfo(sigRes.data || { available: false });
        setAvenants(avRes.data || []);
      } finally {
        setLoadingExtras(false);
      }
    };
    load();
  }, []);

  const openSignedContract = () => {
    if (!signedInfo?.url) return;
    const full = signedInfo.url.startsWith('http')
      ? signedInfo.url
      : `${process.env.REACT_APP_BACKEND_URL}${signedInfo.url}`;
    window.open(full, '_blank', 'noopener,noreferrer');
  };

  const downloadAvenant = async (rec, type) => {
    setBusyAvenant(`${type}-${rec.id}`);
    try {
      const res = type === 'letter'
        ? await tenantAPI.downloadAvenantLetterPdf(rec.id)
        : await tenantAPI.downloadAvenantPdf(rec.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'letter'
        ? `courrier_modification_loyer_${rec.reference}.pdf`
        : `avenant_bail_${rec.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Erreur de téléchargement');
    } finally {
      setBusyAvenant(null);
    }
  };

  const openTenantSignedAvenant = (rec) => {
    if (!rec.signed_avenant_url) return;
    const full = rec.signed_avenant_url.startsWith('http')
      ? rec.signed_avenant_url
      : `${process.env.REACT_APP_BACKEND_URL}${rec.signed_avenant_url}`;
    window.open(full, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-blue-600" />
        Mon Contrat de Location
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Contrat de Location
        </h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Téléchargez votre contrat de location (bail) au format PDF. 
          Ce document officiel contient toutes les conditions de votre location.
        </p>
        
        {profile && (
          <div className="bg-white rounded-lg p-4 mb-6 max-w-sm mx-auto text-left">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Locataire:</span>
                <span className="font-medium">{profile.tenant?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Code:</span>
                <span className="font-mono text-blue-600">{profile.tenant?.client_code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Bien:</span>
                <span className="font-medium">{profile.property?.title}</span>
              </div>
              {profile.property?.monthly_rent && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Loyer mensuel:</span>
                  <span className="font-semibold text-green-600">
                    {profile.property.monthly_rent?.toLocaleString('fr-FR')} FCFA
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <Button 
          onClick={handleDownloadLeaseContract}
          disabled={downloading === 'lease-contract'}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="download-lease-contract-btn"
        >
          {downloading === 'lease-contract' ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Téléchargement...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Télécharger mon contrat
            </>
          )}
        </Button>
        
        {profile?.transaction_id && (
          <Button 
            onClick={() => openSignModal('lease_contract', profile.transaction_id, 'Contrat de Location')}
            variant="outline"
            className="ml-3 border-[#1B3A4B] text-[#1B3A4B] hover:bg-[#1B3A4B]/10"
            data-testid="sign-lease-contract-btn"
          >
            <PenLine className="w-4 h-4 mr-2" />
            Signer le contrat
          </Button>
        )}
      </div>
    </CardContent>
  </Card>

  {/* Attestation de domicile */}
  <Card className="mt-6">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Home className="w-5 h-5 text-[#D4A745]" />
        Attestation de Domicile
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="bg-gradient-to-br from-[#FEF7E0] to-amber-50 rounded-xl p-6 text-center border border-[#D4A745]/30">
        <div className="w-16 h-16 bg-[#D4A745]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Home className="w-8 h-8 text-[#D4A745]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Justifier votre domicile
        </h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Téléchargez à tout moment votre attestation de domicile officielle, délivrée par votre agence,
          pour vos démarches administratives (banque, école, justice, etc.).
        </p>

        {profile && (
          <div className="bg-white rounded-lg p-4 mb-6 max-w-sm mx-auto text-left">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Locataire:</span>
                <span className="font-medium">{profile.tenant?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Adresse:</span>
                <span className="font-medium text-right">
                  {profile.property?.address || profile.property?.title}
                </span>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={handleDownloadResidenceCertificate}
          disabled={downloading === 'residence-certificate'}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="download-residence-certificate-btn"
        >
          {downloading === 'residence-certificate' ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Génération en cours...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Télécharger l&apos;attestation
            </>
          )}
        </Button>
      </div>
    </CardContent>
  </Card>

  {/* Contrat signé (scan) */}
  {signedInfo?.available && (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCheck2 className="w-5 h-5 text-emerald-600" />
          Contrat signé
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl p-5 border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white flex items-start gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg shrink-0">
            <FileCheck2 className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">Votre contrat signé par les deux parties</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Téléversé par l&apos;agence le {signedInfo.uploaded_at ? new Date(signedInfo.uploaded_at).toLocaleDateString('fr-FR') : ''}
            </p>
            <Button
              onClick={openSignedContract}
              className="mt-3 bg-emerald-600 hover:bg-emerald-700"
              size="sm"
              data-testid="view-signed-contract-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Voir le contrat signé
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )}

  {/* Avenants au contrat */}
  {avenants.length > 0 && (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-amber-600" />
          Avenants au contrat ({avenants.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {avenants.map((av) => {
          const isDecrease = av.change_type === 'baisse' || (av.increase_amount || 0) < 0;
          const statusLabel = {
            brouillon: 'Brouillon',
            notifie: 'À signer',
            applique: 'Appliqué',
            annule: 'Annulé',
          }[av.status] || av.status;
          return (
            <div key={av.id} className={`p-4 rounded-xl border ${av.status === 'applique' ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid={`tenant-avenant-${av.id}`}>
              <div className="flex justify-between items-start gap-2 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500">{av.reference}</span>
                    <Badge className={av.status === 'applique' ? 'bg-emerald-600' : av.signed_avenant_url ? 'bg-emerald-600' : 'bg-amber-500'}>
                      {av.signed_avenant_url && av.status !== 'applique' ? 'Signé' : statusLabel}
                    </Badge>
                    {isDecrease ? (
                      <ArrowDownCircle className="w-4 h-4 text-red-600" />
                    ) : (
                      <ArrowUpCircle className="w-4 h-4 text-emerald-600" />
                    )}
                  </div>
                  <p className="text-sm mt-1 font-semibold text-gray-900">
                    {isDecrease ? 'Baisse' : 'Augmentation'} de loyer :
                    <span className={`ml-1 ${isDecrease ? 'text-red-700' : 'text-emerald-700'}`}>
                      {(av.current_rent || 0).toLocaleString('fr-FR')} → {(av.new_rent || 0).toLocaleString('fr-FR')} {av.currency || 'XOF'}
                    </span>
                    <span className="ml-1 text-xs text-gray-500">
                      ({av.increase_percentage >= 0 ? '+' : ''}{(av.increase_percentage || 0).toFixed(1)}%)
                    </span>
                  </p>
                  {av.effective_date && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Date d&apos;effet : {new Date(av.effective_date).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadAvenant(av, 'avenant')}
                  disabled={busyAvenant === `avenant-${av.id}`}
                  data-testid={`tenant-download-avenant-${av.id}`}
                >
                  {busyAvenant === `avenant-${av.id}` ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <FileSignature className="w-4 h-4 mr-1" />
                  )}
                  Avenant officiel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadAvenant(av, 'letter')}
                  disabled={busyAvenant === `letter-${av.id}`}
                >
                  {busyAvenant === `letter-${av.id}` ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-1" />
                  )}
                  Courrier d&apos;info
                </Button>
                {av.signed_avenant_url && (
                  <Button
                    size="sm"
                    onClick={() => openTenantSignedAvenant(av)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`tenant-view-signed-${av.id}`}
                  >
                    <FileCheck2 className="w-4 h-4 mr-1" />
                    Voir signé
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  )}

  {loadingExtras && (
    <div className="mt-6 flex justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
    </div>
  )}
    </>
  );
};

export default TenantContract;
